// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import type {Guild} from '@app/features/guild/models/Guild';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import ContextMenu from '@app/features/ui/state/ContextMenu';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {autorun} from 'mobx';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const logger = new Logger('useCommunityActions');
export const useCommunityActions = (visionaryGuild: Guild | undefined, operatorGuild: Guild | undefined) => {
	const {t} = useLingui();
	const [loadingRejoinCommunity, setLoadingRejoinCommunity] = useState(false);
	const [isCommunityMenuOpen, setIsCommunityMenuOpen] = useState(false);
	const communityButtonRef = useRef<HTMLButtonElement | null>(null);
	const hasVisionaryGuild = Boolean(visionaryGuild);
	const hasOperatorGuild = Boolean(operatorGuild);
	const getFirstViewableChannel = useCallback((guildId: string) => {
		const channels = Channels.getGuildChannels(guildId);
		return channels.find((channel) => channel.type === ChannelTypes.GUILD_TEXT);
	}, []);
	const handleRejoinCommunity = useCallback(
		async (type: 'visionary' | 'operator') => {
			setLoadingRejoinCommunity(true);
			try {
				if (type === 'visionary') {
					await PremiumCommands.rejoinVisionaryGuild();
				} else {
					await PremiumCommands.rejoinOperatorGuild();
				}
				const guild = type === 'visionary' ? visionaryGuild : operatorGuild;
				if (guild) {
					const firstChannel = getFirstViewableChannel(guild.id);
					ModalCommands.popAll();
					NavigationCommands.selectChannel(guild.id, firstChannel?.id);
				}
			} catch (error) {
				logger.error(`Failed to rejoin ${type} guild`, error);
				ToastCommands.error(
					type === 'visionary'
						? t`Failed to rejoin the Visionary community. Please try again.`
						: t`Failed to rejoin the Operators community. Please try again.`,
				);
			} finally {
				setLoadingRejoinCommunity(false);
			}
		},
		[visionaryGuild, operatorGuild, getFirstViewableChannel, t],
	);
	const handleCommunityButtonPointerDown = useCallback((event: React.PointerEvent) => {
		const contextMenu = ContextMenu.contextMenu;
		const isOpen = !!contextMenu && contextMenu.target.target === communityButtonRef.current;
		if (isOpen) {
			event.stopPropagation();
			event.preventDefault();
			ContextMenuCommands.close();
		}
	}, []);
	const handleCommunityButtonClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			const contextMenu = ContextMenu.contextMenu;
			const isOpen = !!contextMenu && contextMenu.target.target === event.currentTarget;
			if (isOpen) {
				return;
			}
			ContextMenuCommands.openFromEvent(event, () => (
				<>
					<MenuItem
						onClick={() => {
							ContextMenuCommands.close();
							if (hasVisionaryGuild && visionaryGuild) {
								const firstChannel = getFirstViewableChannel(visionaryGuild.id);
								ModalCommands.popAll();
								NavigationCommands.selectChannel(visionaryGuild.id, firstChannel?.id);
							} else {
								handleRejoinCommunity('visionary');
							}
						}}
					>
						{hasVisionaryGuild ? t`Open Visionary Community` : t`Join Visionary Community`}
					</MenuItem>
					<MenuItem
						onClick={() => {
							ContextMenuCommands.close();
							if (hasOperatorGuild && operatorGuild) {
								const firstChannel = getFirstViewableChannel(operatorGuild.id);
								ModalCommands.popAll();
								NavigationCommands.selectChannel(operatorGuild.id, firstChannel?.id);
							} else {
								handleRejoinCommunity('operator');
							}
						}}
					>
						{hasOperatorGuild ? t`Open Operators Community` : t`Join Operators Community`}
					</MenuItem>
				</>
			));
		},
		[
			hasVisionaryGuild,
			hasOperatorGuild,
			visionaryGuild,
			operatorGuild,
			getFirstViewableChannel,
			handleRejoinCommunity,
			t,
		],
	);
	useEffect(() => {
		const handleContextMenuChange = () => {
			const contextMenu = ContextMenu.contextMenu;
			const isOpen =
				!!contextMenu && !!communityButtonRef.current && contextMenu.target.target === communityButtonRef.current;
			setIsCommunityMenuOpen(isOpen);
		};
		const disposer = autorun(handleContextMenuChange);
		return () => disposer();
	}, []);
	return {
		loadingRejoinCommunity,
		isCommunityMenuOpen,
		communityButtonRef,
		handleCommunityButtonPointerDown,
		handleCommunityButtonClick,
	};
};
